import { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { PageHeader } from "../components/PageHeader";
import {
  apiCall,
  RouteNotFoundError,
  ApiHttpError,
  type VerifyResponseBody,
} from "../api/client";
import {
  DEFAULT_OWNER_SUBJECT,
  GAOSAFE_AUTHORITY_CHAIN_ID,
  GAOSAFE_AUTHORITY_CHAIN_NAME,
  SIWE_CHAIN_ALLOWLIST,
  chainLabel,
  isAllowedLoginChain,
  WALLETCONNECT_PROJECT_ID,
} from "../config";
import { describeProviderError } from "../auth/provider-error";
import {
  getEvmProvider,
  personalSign,
  requestAccount,
  getCurrentChainId,
  WalletProviderUnavailableError,
} from "../auth/wallet";
import {
  getLastOwnerSubject,
  setLastOwnerSubject,
  setSession,
  getSessionMetadata,
  subscribeSessionMetadata,
  type SessionMetadata,
  _testing as sessionTesting,
} from "../auth/session";
import { parseServerSession, type ServerSessionClaims } from "../auth/session-claims";
import { clearInvalidation } from "../auth/provider-events";
import { BUILD_ID, COMMIT_SHA, BUILD_TIME } from "../buildInfo";
import {
  buildSafeDebugBundle,
  copySafeDebugToClipboard,
} from "../auth/debug";
import { getOrCreateDeviceFingerprintHash } from "../auth/device-fingerprint";
import {
  Banner,
  Button,
  Card,
  Chip,
  Disclosure,
  SectionLabel,
  type Tone,
} from "../ui/primitives";
import { IconWallet } from "../ui/icons";
import { buildSiweMessage } from "../auth/siwe";
import { SessionIdentity } from "../components/SessionIdentity";
import { ReauthNotice } from "../components/ReauthNotice";

/** The one place a failed sign-in is reported, whichever step failed.
 *
 *  There was a real hole here, and collapsing the manual steps behind a
 *  disclosure turned it into a silent failure. `ensureWalletConnected` and
 *  `ensureChainOk` both route their catch through `handlePhaseError(e,
 *  "challenge")`, which writes `challengeError` — but the only renderer of
 *  that field, `ChallengeResultPanel`, early-returns a placeholder unless
 *  `challengeClicked` is true, and only `doChallenge` ever sets that flag.
 *  So an operator clicking the primary button with a locked or missing
 *  wallet got the message written to state and never drawn: previously just
 *  the line "Phase: challenge_failed", and once that line moved into the
 *  diagnostics panel, nothing at all.
 *
 *  This reads PHASE rather than `challengeClicked`, so it fires for every
 *  failing path — connect, chain switch, challenge, verify, session probe —
 *  and shows whichever error field the handler populated. It changes no auth
 *  behaviour; it renders state the page was already computing. */
function LoginFailureNotice({ debug }: { debug: SafeDebug }) {
  const FAILED: ReadonlyArray<Phase> = [
    "challenge_failed",
    "sign_failed",
    "verify_failed",
    "session_probe_failed",
  ];
  const failed = FAILED.includes(debug.phase);
  // A rejected chain switch parks the phase back at `wallet_connected` while
  // leaving the reason in `challengeError`, so phase alone would miss it.
  const strandedReason =
    !failed && debug.challengeOk === null && debug.challengeError !== null
      ? debug.challengeError
      : null;
  if (!failed && strandedReason === null) return null;

  // A SIGNING failure has its own detail and must not fall through to the
  // HTTP-shaped fields — `beErrorMessage` and `challengeError` can hold stale
  // values from an earlier attempt, and pairing one of those with a wallet
  // failure is how a current title ends up describing a previous error.
  const detail =
    debug.phase === "sign_failed"
      ? (debug.signError ?? "The wallet rejected the request without a reason.")
      : (strandedReason ??
        debug.beErrorMessage ??
        debug.challengeError ??
        debug.verifyError ??
        debug.sessionProbeError ??
        "No further detail was reported.");

  const title =
    debug.phase === "sign_failed"
      ? // The wallet, not the server. Nothing was verified because nothing was
        // sent, and the previous wording said the opposite.
        "Wallet signature request failed"
      : debug.phase === "verify_failed"
        ? "Signature verification failed"
        : debug.phase === "session_probe_failed"
          ? "Session was minted but rejected"
          : "Sign-in did not complete";

  return (
    <Banner tone="danger" title={title}>
      {detail}
      {debug.phase === "sign_failed"
        ? debug.providerErrorCode !== null && (
            <>
              {" ("}
              <code>EIP-1193 {debug.providerErrorCode}</code>
              {")"}
            </>
          )
        : debug.beErrorCode !== null && (
            <>
              {" ("}
              <code>{debug.beErrorCode}</code>
              {")"}
            </>
          )}
      {debug.phase === "sign_failed" && debug.providerErrorData !== null && (
        <div style={{ marginTop: 6, fontSize: 11.5, opacity: 0.85 }}>
          <code>data: {debug.providerErrorData}</code>
        </div>
      )}
    </Banner>
  );
}

/** One line of the sign-in status card: what it is, what state it is in, and
 *  the detail that makes the state actionable.
 *
 *  The state is always a WORD in a chip — never a bare colour — so an
 *  operator who is colour-blind, on a washed-out monitor, or simply moving
 *  fast reads the same thing everyone else does. */
function StatusRow({
  label,
  state,
  tone,
  detail,
  action,
}: {
  label: string;
  state: string;
  tone: Tone;
  detail?: React.ReactNode;
  action?: React.ReactNode;
}) {
  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        gap: 12,
        padding: "11px 2px",
        borderBottom: "1px solid var(--border-faint)",
        flexWrap: "wrap",
      }}
    >
      <span style={{ width: 88, flex: "0 0 auto", fontSize: 13, fontWeight: 600 }}>
        {label}
      </span>
      <Chip tone={tone} solid>
        {state}
      </Chip>
      <span
        style={{
          flex: "1 1 200px",
          minWidth: 0,
          fontSize: 12.5,
          color: "var(--fg-dim)",
        }}
      >
        {detail}
      </span>
      {action}
    </div>
  );
}

/** Pull a returnTo destination from `?returnTo=` on /login. We
 *  accept ONLY app-internal absolute paths (starting with `/`) so
 *  a tampered query string can't redirect the operator to an
 *  attacker-controlled URL after login. */
function readReturnToFromSearch(search: string): string | null {
  try {
    const sp = new URLSearchParams(search);
    const raw = sp.get("returnTo");
    if (typeof raw !== "string" || raw === "") return null;
    // Only allow same-origin relative paths.
    if (!raw.startsWith("/") || raw.startsWith("//")) return null;
    return raw;
  } catch {
    return null;
  }
}

// ─── Phase machine ────────────────────────────────────────────────
//
// Explicit transitions so a future reader can audit the gating
// logic at a glance and so each phase can render its own inline
// status under the relevant step.

type Phase =
  | "idle"
  | "wallet_connecting"
  | "wallet_connected"
  | "chain_switching"
  | "chain_ok"
  | "challenge_loading"
  | "challenge_ready"
  | "challenge_failed"
  | "signing"
  | "verify_loading"
  | "session_probing"
  | "session_active"
  | "session_probe_failed"
  // SIGNING AND VERIFYING FAIL SEPARATELY, because they fail in different
  // places for different reasons and only one of them involves the server.
  // They used to share `verify_failed`, so a wallet that refused to sign was
  // reported as "Signature verification failed" for a request that was never
  // sent.
  | "sign_failed"
  | "verify_failed";

const EVM_ADDR_RE = /^0x[0-9a-fA-F]{40}$/;

/** Safe debug snapshot — never contains the bearer token or the
 *  raw signature. */
interface SafeDebug {
  phase: Phase;
  walletProviderDetected: boolean | null;
  walletAddress: string | null;
  walletChainId: number | null;
  expectedChainId: number;
  ownerSubject: string;
  ownerSubjectLength: number;
  /** The wallet method used. `personal_sign` since Admin converged on canonical
   *  Gao SIWE; the retired custom path used `eth_signTypedData_v4`. */
  signerMethod: "personal_sign" | "eth_signTypedData_v4" | null;
  challengeClicked: boolean;
  challengeInFlight: boolean;
  challengeHttpStatus: number | null;
  challengeOk: boolean | null;
  challengeRequestIdPresent: boolean | null;
  challengeError: string | null;
  verifyInFlight: boolean;
  verifyHttpStatus: number | null;
  verifyOk: boolean | null;
  verifyError: string | null;
  beErrorCode: string | null;
  beErrorMessage: string | null;
  lastExceptionName: string | null;
  lastExceptionMessage: string | null;
  /** EIP-1193 rejection detail. Populated ONLY by a provider throw; these are
   *  the fields `String(e)` used to destroy. */
  providerErrorCode: number | null;
  providerErrorMessage: string | null;
  /** A described shape, never the raw `data` value. See auth/provider-error.ts. */
  providerErrorData: string | null;
  providerErrorKind: string | null;
  signError: string | null;
  sessionStorageRowExists: boolean;
  sessionProbeHttpStatus: number | null;
  sessionProbeOk: boolean | null;
  sessionProbeError: string | null;
}

function defaultDebug(): SafeDebug {
  return {
    phase: "idle",
    walletProviderDetected: null,
    walletAddress: null,
    walletChainId: null,
    expectedChainId: GAOSAFE_AUTHORITY_CHAIN_ID,
    ownerSubject: "",
    ownerSubjectLength: 0,
    signerMethod: null,
    challengeClicked: false,
    challengeInFlight: false,
    challengeHttpStatus: null,
    challengeOk: null,
    challengeRequestIdPresent: null,
    challengeError: null,
    verifyInFlight: false,
    verifyHttpStatus: null,
    verifyOk: null,
    verifyError: null,
    beErrorCode: null,
    beErrorMessage: null,
    lastExceptionName: null,
    lastExceptionMessage: null,
    providerErrorCode: null,
    providerErrorMessage: null,
    providerErrorData: null,
    providerErrorKind: null,
    signError: null,
    sessionStorageRowExists: false,
    sessionProbeHttpStatus: null,
    sessionProbeOk: null,
    sessionProbeError: null,
  };
}

/** Compute the initial value for the ownerSubject input. Priority:
 *    1. Last entered value persisted in sessionStorage (so a
 *       returnTo round-trip doesn't blank the field).
 *    2. The dev/test default `op-cb-2026`.
 *  Both rules are documented in the operator brief §A. */
function initialOwnerSubject(): string {
  const last = getLastOwnerSubject();
  if (typeof last === "string" && last !== "") return last;
  return DEFAULT_OWNER_SUBJECT;
}

export function Login() {
  const navigate = useNavigate();
  const location = useLocation();
  const returnTo = readReturnToFromSearch(location.search);
  const [ownerSubject, setOwnerSubjectState] = useState<string>(initialOwnerSubject);
  // The live session, subscribed rather than read once: a logout, an expiry,
  // or an `accountsChanged` invalidation must flip this screen back to the
  // connect form without a reload.
  const [activeSession, setActiveSession] = useState<SessionMetadata | null>(
    getSessionMetadata(),
  );
  useEffect(() => subscribeSessionMetadata(setActiveSession), []);
  const [walletAddress, setWalletAddress] = useState<string | null>(null);
  const [chainId, setChainId] = useState<number | null>(null);
  const [nonce, setNonce] = useState<string | null>(null);
  const [debug, setDebug] = useState<SafeDebug>(defaultDebug);
  /** This browser's device-fingerprint hash, for the Device status row.
   *
   *  Three states, and the distinction matters: `undefined` = still being
   *  computed, `null` = this browser cannot produce one (no `crypto.subtle`
   *  or no `localStorage`), a string = ready.
   *
   *  Read-only and purely presentational. The sign-in path computes its own
   *  hash at verify time from the same helper — this does not feed it, and
   *  removing this row would not change a single request. It is also why the
   *  row says "Ready"/"Unavailable" rather than "Trusted"/"Untrusted": what
   *  the SERVER has bound is not knowable until after a successful verify,
   *  and a chip claiming otherwise would be inventing state. */
  const [deviceHash, setDeviceHash] = useState<string | null | undefined>(undefined);
  useEffect(() => {
    let live = true;
    void getOrCreateDeviceFingerprintHash()
      .then((h) => {
        if (live) setDeviceHash(h);
      })
      .catch(() => {
        if (live) setDeviceHash(null);
      });
    return () => {
      live = false;
    };
  }, []);
  // Capture-able element for the "Copy safe debug" button.
  const debugRef = useRef<SafeDebug>(defaultDebug());
  debugRef.current = debug;

  function setOwnerSubject(next: string): void {
    setOwnerSubjectState(next);
    // Persist immediately so an accidental nav back to AuthRequired
    // (or a tab refresh) doesn't lose the value. We persist the
    // raw input (with whitespace) so the operator sees the same
    // string they typed; the trim happens at request-build time.
    setLastOwnerSubject(next);
  }

  // Detect provider + sessionStorage row presence on mount.
  useEffect(() => {
    const present =
      typeof window !== "undefined" &&
      typeof (window as unknown as { ethereum?: unknown }).ethereum !== "undefined";
    let rowExists = false;
    try {
      rowExists =
        typeof window !== "undefined" &&
        !!window.sessionStorage &&
        window.sessionStorage.getItem(sessionTesting.STORAGE_KEY) !== null;
    } catch {
      rowExists = false;
    }
    setDebug((d) => ({
      ...d,
      walletProviderDetected: present,
      sessionStorageRowExists: rowExists,
    }));
  }, []);

  // Mirror owner subject into debug so the operator can verify the
  // EXACT bytes the SPA captured (catches trailing-space / case
  // issues from mobile keyboards).
  useEffect(() => {
    setDebug((d) => ({
      ...d,
      ownerSubject,
      ownerSubjectLength: ownerSubject.length,
    }));
  }, [ownerSubject]);

  // ALLOWLIST MEMBERSHIP, NOT EQUALITY WITH THE AUTHORITY CHAIN.
  //
  // This used to read `chainId === REQUIRED_CHAIN_ID`, so an operator on Base
  // mainnet — a chain the server accepts and signs on happily — was shown a red
  // "Wrong chain" and offered a switch to 84532. Being on the authority chain
  // is not a login requirement and never was; the server decides authority on
  // 84532 no matter what network the wallet is pointed at.
  const chainAllowed = isAllowedLoginChain(chainId);
  const ownerSubjectValid =
    ownerSubject.trim() !== "" && ownerSubject.length <= 256;
  const walletValid = walletAddress !== null && EVM_ADDR_RE.test(walletAddress);
  const isBusy =
    debug.phase === "wallet_connecting" ||
    debug.phase === "chain_switching" ||
    debug.phase === "challenge_loading" ||
    debug.phase === "signing" ||
    debug.phase === "verify_loading" ||
    debug.phase === "session_probing";

  // NOT gated on `onChainOk`. Canonical SIWE accepts any chain on the issuer's
  // allowlist and the authority chain is server-side, so requiring 84532 here
  // would reimpose exactly the constraint this convergence removed.
  const canRequestChallenge = walletValid && !isBusy;
  const canSignAndVerify = nonce !== null && walletValid && !isBusy;
  const canPrimaryLogin = ownerSubjectValid && !isBusy;

  // ─── Atomic flow steps ───────────────────────────────────────

  async function ensureWalletConnected(): Promise<string | null> {
    setDebug((d) => ({ ...d, phase: "wallet_connecting" }));
    try {
      const provider = getEvmProvider();
      const addr = await requestAccount(provider);
      setWalletAddress(addr);
      const cid = await getCurrentChainId(provider);
      setChainId(cid);
      setDebug((d) => ({
        ...d,
        walletProviderDetected: true,
        walletAddress: addr,
        walletChainId: cid,
        phase: isAllowedLoginChain(cid) ? "chain_ok" : "wallet_connected",
      }));
      return addr;
    } catch (e) {
      handlePhaseError(e, "challenge");
      return null;
    }
  }

  // REMOVED: `ensureChainOk` / `switchToBaseSepolia`.
  //
  // It asked the wallet to move to 84532 whenever the connected chain was not
  // 84532. Under the ratified chain law there is nothing to "ensure": every
  // chain on the canonical SIWE allowlist is a valid chain to log in from, and
  // pushing an operator to the authority chain asserts a requirement that does
  // not exist. The console does not switch anyone's network; an operator who
  // wants a different network changes it in their wallet.

  /**
   * Canonical Gao nonce. `POST /v2/auth/nonce`, the same route every other Gao
   * product calls — Admin has no challenge endpoint of its own any more.
   *
   * The chainId sent is the one the WALLET is on. It binds the nonce and scopes
   * the EIP-4361 message; it does not choose an operator and it is not the
   * GaoSafe authority chain, which lives server-side and is never sent from here.
   */
  async function doNonce(addr: string, walletChainId: number): Promise<string | null> {
    setDebug((d) => ({
      ...d,
      phase: "challenge_loading",
      challengeClicked: true,
      challengeInFlight: true,
      challengeHttpStatus: null,
      challengeOk: null,
      challengeRequestIdPresent: null,
      challengeError: null,
      beErrorCode: null,
      beErrorMessage: null,
      lastExceptionName: null,
      lastExceptionMessage: null,
    }));
    try {
      const resp = await apiCall<{ nonce?: unknown; expiresAt?: unknown }>(
        "/v2/auth/nonce",
        { method: "POST", body: { address: addr.toLowerCase(), chainId: walletChainId }, bearer: null },
      );
      const nonce = typeof resp.nonce === "string" ? resp.nonce : "";
      if (nonce === "") {
        setDebug((d) => ({
          ...d,
          phase: "challenge_failed",
          challengeInFlight: false,
          challengeHttpStatus: 200,
          challengeOk: false,
          challengeError: "Nonce endpoint returned 200 with no nonce.",
        }));
        return null;
      }
      setDebug((d) => ({
        ...d,
        phase: "challenge_ready",
        challengeInFlight: false,
        challengeHttpStatus: 200,
        challengeOk: true,
        challengeRequestIdPresent: true,
        challengeError: null,
      }));
      return nonce;
    } catch (e) {
      handlePhaseError(e, "challenge");
      return null;
    }
  }

  async function doSignAndVerify(
    addr: string,
    subject: string,
    nonce: string,
    walletChainId: number,
  ): Promise<boolean> {
    setDebug((d) => ({
      ...d,
      phase: "signing",
      signerMethod: "personal_sign",
      verifyInFlight: false,
      verifyHttpStatus: null,
      verifyOk: null,
      verifyError: null,
      beErrorCode: null,
      beErrorMessage: null,
      providerErrorCode: null,
      providerErrorMessage: null,
      providerErrorData: null,
      providerErrorKind: null,
      signError: null,
    }));
    // ── SIGNING, in its OWN try ────────────────────────────────────────────
    //
    // Everything up to and including `personal_sign` happens here. A failure in
    // this block never reached the network, so it must never be reported as a
    // verification result — that mislabelling is what sent a whole acceptance
    // round chasing a backend that was never called.
    let message: string;
    let signature: string;
    try {
      const provider = getEvmProvider();
      // The canonical EIP-4361 string, signed with `personal_sign` — the same
      // message shape and the same signing method every other Gao product uses.
      // `domain` and `uri` come from config, never from `window.location`.
      message = buildSiweMessage({
        address: addr,
        chainId: walletChainId,
        nonce,
        issuedAt: new Date(),
      });
      signature = await personalSign(provider, message, addr);
    } catch (e) {
      handleSignError(e);
      return false;
    }

    // ── VERIFYING, from here on ────────────────────────────────────────────
    try {
      setDebug((d) => ({
        ...d,
        phase: "verify_loading",
        verifyInFlight: true,
      }));
      // Compute the browser-scoped device fingerprint hash before
      // building the verify body. The BE BFF gate
      // (`enforceAdminControlAccess` in gao-id-worker) requires every
      // session to be device-bound — the verify path mints a
      // device-unbound session only when no fingerprint is supplied
      // AND `ADMIN_REQUIRE_TRUSTED_DEVICE` is not "true", and that
      // shape is then rejected by every downstream `/v2/admin/bff/*`
      // call. Sending the fingerprint ensures the mint either binds
      // a known trusted device (success path) or surfaces the
      // documented `device_pending_trust` refusal so the operator
      // can approve via `POST /v2/admin/owner/devices/:id/trust` and
      // retry. See `src/auth/device-fingerprint.ts` for the storage
      // + hashing design.
      const fingerprintHash = await getOrCreateDeviceFingerprintHash();
      // MESSAGE AND SIGNATURE, AND NOTHING ELSE THAT MATTERS.
      //
      // `ownerSubject`, `authorityVault`, `walletAddress`, `chainId` and
      // `membership` were all fields on the retired custom path. The server now
      // derives every one of them — the operator from the recovered signer, the
      // vault and authority chain from its own configuration — so sending them
      // would be sending them nowhere. The device fingerprint stays: it is
      // recorded, never required, and it is not authority.
      const body = {
        message,
        signature,
        ...(fingerprintHash ? { deviceFingerprintHash: fingerprintHash } : {}),
      };
      const verify = await apiCall<VerifyResponseBody>(
        "/v2/admin/owner/auth/verify",
        { method: "POST", body, bearer: null },
      );
      const tok = extractSessionToken(verify);
      if (tok === null) {
        setDebug((d) => ({
          ...d,
          phase: "verify_failed",
          verifyInFlight: false,
          verifyHttpStatus: 200,
          verifyOk: false,
          verifyError:
            "Verify returned 2xx but no session token field was present in the response.",
        }));
        return false;
      }
      const expiresAt = extractExpiresAtMs(verify);
      // Verify minted a token. Per operator brief §D, BEFORE we
      // mark the session active we MUST round-trip it through the
      // BFF session endpoint — otherwise a BE that mints a token
      // its own /v2/admin/bff/session refuses would let us claim
      // login success, navigate to a protected route, and then
      // have the SPA-wide 401 handler kill the just-minted session
      // before the operator's first interaction.
      setDebug((d) => ({
        ...d,
        phase: "session_probing",
        verifyInFlight: false,
        verifyHttpStatus: 200,
        verifyOk: true,
      }));
      const claims = await doSessionProbe(tok);
      if (claims === null) return false;
      // §5. The authority written here is the one the SERVER reported on the
      // probe we just made — never the connected wallet, never `REQUIRED_CHAIN_ID`,
      // never a constant in this bundle. `addr` and `subject` remain what they
      // always were, the operator's INPUT to the login; if the server disagrees
      // with either, the server's values are what the console will show.
      setSession({
        sessionToken: tok,
        walletAddress: addr.toLowerCase(),
        // §5: the server is authority for the operator. `subject` was the
        // operator's INPUT; the projection is the resolution. They normally
        // agree — when they do not, what the console shows is the resolution.
        ownerSubject: claims.ownerSubject ?? subject.trim(),
        savedAt: Date.now(),
        expiresAt,
        authority: claims.authority,
      });
      // A completed login is the ONE thing that clears the invalidation latch.
      // See `src/auth/provider-events.ts`: switching back to the original
      // account must not resurrect a session, so nothing else may clear it.
      clearInvalidation();
      setDebug((d) => ({
        ...d,
        phase: "session_active",
        sessionStorageRowExists: true,
      }));
      // After login: prefer the returnTo destination supplied by
      // the AuthRequired guard. Falls back to the Treasury Ledger CRM
      // dashboard (the primary admin experience).
      navigate(returnTo ?? "/dashboard", { replace: true });
      return true;
    } catch (e) {
      handlePhaseError(e, "verify");
      return false;
    }
  }

  /** Round-trip the freshly-minted bearer through the BFF session
   *  endpoint. Returns the server's session claims on 200; `null` on
   *  401 / any other failure, with the inline error surface populated.
   *
   *  The probe was already a required step — it catches a backend that mints
   *  a token its own `/session` route refuses. It now also carries the
   *  authority: this response is the console's only source for which vault,
   *  which chain and which signer the session was minted under. */
  async function doSessionProbe(tok: string): Promise<ServerSessionClaims | null> {
    try {
      const body = await apiCall<unknown>("/v2/admin/bff/session", {
        method: "GET",
        // Inject the bearer explicitly — the session store is NOT
        // yet populated, so the auto-bearer path would send no
        // Authorization header.
        bearer: tok,
      });
      const claims = parseServerSession(body);
      if (claims === null) {
        // A 200 whose body is not a session envelope. Treated as a failure
        // rather than as a session with empty claims — the alternative is a
        // console that renders "Authority —" and calls it a successful login.
        setDebug((d) => ({
          ...d,
          phase: "session_probe_failed",
          sessionProbeHttpStatus: 200,
          sessionProbeOk: false,
          sessionProbeError:
            "BE /v2/admin/bff/session returned 200 with no session object.",
        }));
        return null;
      }
      setDebug((d) => ({
        ...d,
        sessionProbeHttpStatus: 200,
        sessionProbeOk: true,
        sessionProbeError: null,
      }));
      return claims;
    } catch (e) {
      // On 401, apiCall already cleared the (empty) session and
      // flipped the expired flag. Surface a distinct error so the
      // operator can tell this apart from a verify failure.
      let httpStatus: number | null = null;
      let detail = "Unknown session-probe error";
      if (e instanceof ApiHttpError) {
        httpStatus = e.status;
        detail =
          e.status === 401
            ? "BE minted a token but /v2/admin/bff/session rejected it."
            : `BE /v2/admin/bff/session returned HTTP ${e.status}.`;
      } else if (e instanceof RouteNotFoundError) {
        detail = "BE route missing: /v2/admin/bff/session.";
      } else if (e instanceof TypeError) {
        detail = `Network or CORS error on session probe: ${e.message}.`;
      } else if (e instanceof Error) {
        detail = e.message;
      }
      // No setSession call has occurred — there is nothing to
      // clear locally. The api client has already recorded the
      // 401 path + clear reason via markSessionExpired().
      setDebug((d) => ({
        ...d,
        phase: "session_probe_failed",
        sessionProbeHttpStatus: httpStatus,
        sessionProbeOk: false,
        sessionProbeError: detail,
      }));
      return null;
    }
  }

  // ─── Primary one-button flow ────────────────────────────────

  async function onPrimaryLogin() {
    if (isBusy) return;
    const addr = walletAddress ?? (await ensureWalletConnected());
    if (!addr) return;
    // NO FORCED CHAIN SWITCH.
    //
    // The console used to switch the wallet to 84532 before it would sign,
    // because its custom login object carried the authority chain and the
    // server rebuilt the digest against it. Canonical Gao SIWE does not work
    // that way: `chainId` is protocol metadata that binds the nonce, the issuer
    // accepts any chain on its allowlist, and identity is the address. The
    // GaoSafe authority chain is 84532 and stays entirely server-side.
    //
    // So an operator on Base mainnet signs a message for Base mainnet, and the
    // server still asks the vault on 84532 whether that signer is an owner.
    const live = await readChainId();
    // SIGN ON THE CHAIN THE WALLET IS ACTUALLY ON, or refuse.
    //
    // There is no fallback to a default chain. The previous `live ?? 84532`
    // would have signed a message CLAIMING the authority chain while the wallet
    // sat somewhere else — a false statement in the one field canonical SIWE
    // binds the nonce with. If the chain cannot be read, or the wallet is on a
    // chain the issuer would refuse, the honest move is to say so and stop.
    if (!isAllowedLoginChain(live)) {
      setDebug((d) => ({
        ...d,
        phase: "wallet_connected",
        walletChainId: live,
        challengeError:
          live === null
            ? "Could not read the wallet's network."
            : `Unsupported network (chainId ${live}). Switch to a supported network in your wallet.`,
      }));
      return;
    }
    const nonce = await doNonce(addr, live);
    if (nonce === null) return;
    await doSignAndVerify(addr, ownerSubject, nonce, live);
  }

  /** The chain the wallet is on right now, or null if it cannot be read. */
  async function readChainId(): Promise<number | null> {
    try {
      const id = await getCurrentChainId(getEvmProvider());
      setChainId(id);
      return id;
    } catch {
      return null;
    }
  }

  // ─── Per-step manual handlers (kept for diagnosis) ──────────

  async function onConnect() {
    await ensureWalletConnected();
  }
  async function onRequestChallenge() {
    if (!canRequestChallenge || !walletAddress) return;
    const live = await readChainId();
    if (!isAllowedLoginChain(live)) return;
    const n = await doNonce(walletAddress, live);
    if (n !== null) setNonce(n);
  }
  async function onSignAndVerify() {
    if (!canSignAndVerify || !walletAddress || nonce === null) return;
    const live = await readChainId();
    if (!isAllowedLoginChain(live)) return;
    await doSignAndVerify(walletAddress, ownerSubject, nonce, live);
  }

  // ─── Error mapping ──────────────────────────────────────────

  /**
   * A failure at the WALLET, before anything was sent.
   *
   * Kept separate from `handlePhaseError` on purpose. That function's job is to
   * explain an HTTP exchange — status, backend code, backend message — and none
   * of those exist here. Forcing a provider rejection through it is what
   * produced "Signature verification failed / [object Object]": a title about a
   * server that was never called, and a detail that was `String(e)` on an
   * object.
   *
   * `describeProviderError` returns null when the throw is not provider-shaped,
   * and then the ordinary `Error` handling applies — a `SiweMessageError` from
   * the builder, say, is our bug and should read like one.
   */
  function handleSignError(e: unknown): void {
    const provider = describeProviderError(e);
    const exName =
      typeof e === "object" && e !== null
        ? ((e as { name?: unknown }).name ?? null)
        : null;
    const fallback =
      e instanceof Error ? e.message : "The wallet rejected the request without a reason.";
    setDebug((d) => ({
      ...d,
      phase: "sign_failed",
      verifyInFlight: false,
      // Explicitly NOT set: verifyHttpStatus, verifyOk, beErrorCode,
      // beErrorMessage. No request was made, so claiming anything about one —
      // even `false` — would be inventing a result.
      signError: provider?.summary ?? fallback,
      providerErrorCode: provider?.code ?? null,
      providerErrorMessage: provider?.message ?? null,
      providerErrorData: provider?.data ?? null,
      providerErrorKind: provider?.kind ?? null,
      lastExceptionName: typeof exName === "string" ? exName : null,
      lastExceptionMessage: e instanceof Error ? e.message : (provider?.message ?? null),
    }));
  }

  function handlePhaseError(e: unknown, phase: "challenge" | "verify"): void {
    let message = "Unknown error";
    let providerFailure: ReturnType<typeof describeProviderError> = null;
    let beErrorCode: string | null = null;
    let beErrorMessage: string | null = null;
    let httpStatus: number | null = null;
    const exName =
      typeof e === "object" && e !== null
        ? (e as { name?: unknown }).name ?? null
        : null;
    if (e instanceof RouteNotFoundError) {
      message = `BE route missing: ${e.message}`;
    } else if (e instanceof ApiHttpError) {
      httpStatus = e.status;
      const b = e.body;
      if (b !== null && typeof b === "object") {
        const r = b as Record<string, unknown>;
        if (typeof r.error === "string") beErrorCode = r.error;
        if (typeof r.message === "string") beErrorMessage = r.message;
      }
      const hint =
        e.status === 401 && phase === "verify"
          ? " (wallet not authorized for admin owner OR signature mismatch)"
          : "";
      message = `BE HTTP ${e.status}${beErrorCode ? ` ${beErrorCode}` : ""}${beErrorMessage ? `: ${beErrorMessage}` : ""}${hint}`;
    } else if (e instanceof WalletProviderUnavailableError) {
      message = e.message;
    } else if (e instanceof TypeError) {
      // Cross-origin fetch failure / DNS / CORS / mobile webview
      // network error all surface as TypeError on fetch in browsers.
      message = `Network or CORS error: ${e.message}. Check Origin allow-list on BE.`;
    } else if (e instanceof Error) {
      message = e.message;
    } else {
      // A wallet rejects with a plain EIP-1193 object, never an Error, and the
      // CONNECT step routes through here. `String(e)` on that object rendered
      // "[object Object]" on the real DEV console the first time a connect
      // prompt was refused — the operator was told nothing. Describe it the
      // way the sign step already does; only a value the describer cannot
      // read falls through to a stringification.
      const provider = describeProviderError(e);
      message = provider?.summary ?? String(e);
      if (provider !== null) providerFailure = provider;
    }
    setDebug((d) => ({
      ...d,
      phase: phase === "challenge" ? "challenge_failed" : "verify_failed",
      providerErrorCode: providerFailure?.code ?? d.providerErrorCode,
      providerErrorKind: providerFailure?.kind ?? d.providerErrorKind,
      providerErrorMessage: providerFailure?.message ?? d.providerErrorMessage,
      challengeInFlight: phase === "challenge" ? false : d.challengeInFlight,
      challengeHttpStatus: phase === "challenge" ? httpStatus : d.challengeHttpStatus,
      challengeOk: phase === "challenge" ? false : d.challengeOk,
      challengeError: phase === "challenge" ? message : d.challengeError,
      verifyInFlight: phase === "verify" ? false : d.verifyInFlight,
      verifyHttpStatus: phase === "verify" ? httpStatus : d.verifyHttpStatus,
      verifyOk: phase === "verify" ? false : d.verifyOk,
      verifyError: phase === "verify" ? message : d.verifyError,
      beErrorCode,
      beErrorMessage,
      lastExceptionName: typeof exName === "string" ? exName : null,
      lastExceptionMessage: e instanceof Error ? e.message : null,
    }));
  }

  function onCopySafeDebug() {
    const bundle = buildSafeDebugBundle({
      route: location.pathname,
      returnTo,
      login: {
        phase: debug.phase,
        walletProviderDetected: debug.walletProviderDetected,
        walletAddress: debug.walletAddress,
        walletChainId: debug.walletChainId,
        expectedChainId: debug.expectedChainId,
        ownerSubject: debug.ownerSubject,
        ownerSubjectLength: debug.ownerSubjectLength,
        challengeHttpStatus: debug.challengeHttpStatus,
        challengeOk: debug.challengeOk,
        challengeError: debug.challengeError,
        verifyHttpStatus: debug.verifyHttpStatus,
        verifyOk: debug.verifyOk,
        verifyError: debug.verifyError,
        sessionProbeHttpStatus: debug.sessionProbeHttpStatus,
        sessionProbeOk: debug.sessionProbeOk,
        sessionProbeError: debug.sessionProbeError,
        beErrorCode: debug.beErrorCode,
        beErrorMessage: debug.beErrorMessage,
        providerErrorCode: debug.providerErrorCode,
        providerErrorMessage: debug.providerErrorMessage,
        providerErrorData: debug.providerErrorData,
        providerErrorKind: debug.providerErrorKind,
        signError: debug.signError,
      },
    });
    copySafeDebugToClipboard(bundle);
  }

  const primarySubjectLabel = ownerSubjectValid
    ? ownerSubject.trim()
    : DEFAULT_OWNER_SUBJECT;
  // ── What the operator sees, in order ──────────────────────────────────
  //
  // The previous version of this screen rendered nine peer `<fieldset>`
  // sections down a 2049px page: owner subject, the primary button, a
  // "manual step-by-step (diagnostic)" preamble, four numbered manual steps,
  // a 28-row debug table, and a WalletConnect notice — all open, all the
  // time, all at the same visual weight. The one control an operator uses
  // 99% of the time was the fourth thing on the page and looked like the
  // other eleven buttons around it.
  //
  // The rebuild is a re-ordering, not a deletion. Every control, every
  // status line and all 28 debug rows are still rendered and still carry
  // their exact labels — `Login.test.tsx` drives the manual steps by those
  // literal strings ("Connect injected wallet", "POST /v2/auth/nonce",
  // "Sign (personal_sign) + POST /verify"), and those tests are the regression
  // net for the auth protocol itself, so the labels are pinned by something
  // more important than layout. The last two changed when Admin converged onto
  // canonical Gao SIWE; the pinning did not.
  //
  // What changed is altitude:
  //   1. sign-in card       — subject + ONE dominant CTA
  //   2. status card        — wallet / network / challenge / session as rows
  //   3. <details>          — the four manual steps, closed by default
  //   4. <details>          — the safe debug report + table, closed
  //   5. <details>          — WalletConnect + build stamp, closed
  //
  // The manual steps stay in the DOM when closed, which is why the tests
  // keep passing and why an operator debugging a failed login still finds
  // them with the browser's own find-in-page.  // ── What the operator sees, in order ──────────────────────────────────
  //
  // Action-first: the three facts that decide whether sign-in can succeed,
  // then ONE control, then everything else collapsed.
  //
  //   1. three status rows  — wallet / network / device, each a WORD + tone
  //   2. the CTA            — filled, full width, nothing competing with it
  //   3. owner subject      — secondary, below the button
  //   4. <details> ×3       — the manual steps, the 28-row debug table, and
  //                           the WalletConnect + build stamp
  //
  // Nothing was deleted in getting here. Every control and every debug row
  // that the original nine-fieldset page rendered is still on this screen,
  // and the manual steps keep their exact labels because `Login.test.tsx`
  // drives the auth protocol through them.
  return (
    <div style={{ display: "grid", gap: 16, maxWidth: 760 }}>
      <PageHeader
        title="Operator access"
        subtitle="Sign in with a GaoSafe vault signer."
        meta={
          <Chip tone="accent" solid>
            DEV / TEST
          </Chip>
        }
      />

      {/* Already signed in. Reached by navigating to /login with a live
          session — which is exactly what an operator does when they want to
          check who the console thinks they are. Showing the connect form
          instead would answer a question nobody asked. */}
      {activeSession !== null && (
        <Card>
          <div style={{ display: "grid", gap: 12 }}>
            <SectionLabel>Signed in</SectionLabel>
            <SessionIdentity session={activeSession} />
          </div>
        </Card>
      )}

      <ReauthNotice />

      <NoWalletNotice detected={debug.walletProviderDetected} />

      {returnTo !== null && (
        <Banner tone="accent" compact>
          After login you will return to <code>{returnTo}</code>.
        </Banner>
      )}

      <Card>
        <div style={{ display: "grid", gap: 18 }}>
          {/* ── 1. The three preconditions ──────────────────────────────
              Lifted out of the numbered manual steps and stated as rows, so
              an operator reads whether sign-in can work without running
              anything. Each row is a word plus a tone, never a tone alone. */}
          <div style={{ display: "grid", gap: 2 }}>
            <StatusRow
              label="Wallet"
              tone={
                walletValid ? "ok" : debug.walletProviderDetected === false ? "danger" : "neutral"
              }
              state={
                walletValid
                  ? "Connected"
                  : debug.walletProviderDetected === false
                    ? "No provider"
                    : "Not connected"
              }
              detail={
                walletAddress !== null ? (
                  <code className="g-mono" title={walletAddress}>
                    {walletAddress.slice(0, 10)}…{walletAddress.slice(-8)}
                  </code>
                ) : (
                  "MetaMask, Rabby or Frame"
                )
              }
            />
            {/* THE CONNECTED WALLET'S NETWORK, reported as what it is.
                It is not graded against the authority chain, and it carries no
                action: the console never switches an operator's network. The
                only failing state is a chain canonical SIWE would refuse. */}
            <StatusRow
              label="Network"
              tone={chainId === null ? "neutral" : chainAllowed ? "ok" : "danger"}
              state={
                chainId === null
                  ? "Unknown"
                  : chainAllowed
                    ? chainLabel(chainId)
                    : "Unsupported network"
              }
              detail={chainId === null ? "Connect a wallet" : `chainId ${chainId}`}
            />
            <StatusRow
              label="Authority"
              // Pre-login this names the AUTHORITY THIS CONSOLE TALKS TO — the
              // deployment target — and deliberately not an address. The vault
              // is a server fact; asserting one here from a bundled constant
              // is the exact client-side authority claim §5 removes. The real
              // address appears above, after the server has stated it.
              tone={activeSession !== null ? "ok" : "neutral"}
              state={activeSession !== null ? "Bound" : "GaoSafe TEST"}
              detail={
                activeSession !== null
                  ? "Session is bound to the vault named above"
                  : "Confirmed by the server when you sign in"
              }
            />
            <StatusRow
              label="Device"
              // What this row can HONESTLY say. The fingerprint is computed
              // in this browser; whether the backend has bound a session to
              // it is not knowable until after a successful verify. So the
              // states are "this device can be identified" / "it cannot",
              // not "trusted" / "untrusted" — which would be a claim about
              // server state this page has not read.
              tone={
                deviceHash === undefined ? "neutral" : deviceHash === null ? "warn" : "ok"
              }
              state={
                deviceHash === undefined
                  ? "Checking"
                  : deviceHash === null
                    ? "Unavailable"
                    : "Ready"
              }
              detail={
                deviceHash === undefined ? (
                  "Computing this device's fingerprint"
                ) : deviceHash === null ? (
                  "Sign-in will not be bound to this device"
                ) : (
                  <code className="g-mono" title={deviceHash}>
                    {deviceHash.slice(0, 12)}…
                  </code>
                )
              }
            />
          </div>

          {/* ── 2. The one control ─────────────────────────────────────── */}
          <Button
            variant="primary"
            size="lg"
            onClick={onPrimaryLogin}
            disabled={!canPrimaryLogin}
            full
            icon={<IconWallet size={18} />}
          >
            {isBusy ? `Working… (${debug.phase})` : `Connect & sign in as ${primarySubjectLabel}`}
          </Button>

          <LoginFailureNotice debug={debug} />
          <SessionProbePanel debug={debug} />

          {/* ── 3. Owner subject, secondary ─────────────────────────────
              It stays a real, always-rendered, labelled input — the operator
              does change it, and `Login.test.tsx` drives it by label — but it
              no longer opens the page above the button it configures. */}
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 10,
              flexWrap: "wrap",
              paddingTop: 4,
              borderTop: "1px solid var(--border-faint)",
            }}
          >
            <label
              htmlFor="owner-subject"
              style={{
                fontSize: 12.5,
                color: "var(--fg-dim)",
                flex: "0 0 auto",
                paddingTop: 12,
              }}
            >
              Owner subject
            </label>
            <input
              id="owner-subject"
              type="text"
              autoCapitalize="off"
              autoCorrect="off"
              spellCheck={false}
              value={ownerSubject}
              onChange={(e) => setOwnerSubject(e.target.value)}
              placeholder={DEFAULT_OWNER_SUBJECT}
              aria-label="Owner subject"
              aria-invalid={!ownerSubjectValid}
              style={{
                flex: "1 1 200px",
                width: "auto",
                marginTop: 12,
                fontFamily: "var(--font-mono)",
                fontSize: 13,
                padding: "6px 9px",
              }}
            />
          </div>
          {!ownerSubjectValid && (
            <p role="alert" style={{ fontSize: 12, color: "var(--danger)", margin: 0 }}>
              Owner subject is required. Login is disabled until this is set.
            </p>
          )}
        </div>
      </Card>

      <Disclosure summary="Advanced diagnostics">
        <p style={{ fontSize: 12.5, color: "var(--fg-dim)", marginTop: 8 }}>
          Challenge, digest, session ids and RPC detail. Run the steps manually only
          if the primary button fails — each shows its own inline status.
        </p>

        <Section title="1. Connect wallet">
          <div style={rowGap}>
            <button onClick={onConnect} disabled={isBusy || debug.walletProviderDetected === false}>
              {walletAddress === null ? "Connect injected wallet" : "Reconnect"}
            </button>
            {walletAddress && <code style={{ wordBreak: "break-all" }}>{walletAddress}</code>}
          </div>
          <InlineStatus
            ok={walletValid}
            loading={debug.phase === "wallet_connecting"}
            okMessage={`Wallet ${walletAddress ?? ""}`}
            idleMessage="No wallet connected yet."
          />
        </Section>

        <Section title="2. Network (connected wallet chain)">
          <div style={rowGap}>
            <span>
              Detected chainId: <code>{chainId === null ? "—" : chainId}</code>{" "}
              {chainId !== null &&
                (chainAllowed ? (
                  <span style={{ color: "var(--ok)" }}>{chainLabel(chainId)}</span>
                ) : (
                  <span style={{ color: "var(--danger)" }}>unsupported network</span>
                ))}
            </span>
          </div>
          {/* No switch control. See the note where `ensureChainOk` used to be. */}
          <InlineStatus
            ok={chainAllowed}
            loading={false}
            okMessage={`Signing on ${chainLabel(chainId)} (${chainId}). Authority is read on ${GAOSAFE_AUTHORITY_CHAIN_NAME} (${GAOSAFE_AUTHORITY_CHAIN_ID}) server-side.`}
            idleMessage="Connect wallet to detect chain."
          />
          <p style={{ fontSize: 11.5, color: "var(--fg-dim)", margin: "6px 0 0" }}>
            Allowed login chains: {SIWE_CHAIN_ALLOWLIST.join(", ")}
          </p>
        </Section>

        <Section title="3. Request nonce">
          <button onClick={onRequestChallenge} disabled={!canRequestChallenge}>
            {debug.phase === "challenge_loading" ? "Loading…" : "POST /v2/auth/nonce"}
          </button>
          {!canRequestChallenge && !isBusy && (
            <p style={{ fontSize: 11.5, color: "var(--fg-dim)", margin: "6px 0 0" }}>
              Disabled until: {!ownerSubjectValid && "owner subject is set, "}
              {!walletValid && "wallet is connected, "}
              {!chainAllowed && "wallet is on a supported network."}
            </p>
          )}
          {/* Inline status — visible IMMEDIATELY under the button, so the
              operator never has to scroll to see what happened. */}
          <ChallengeResultPanel debug={debug} />
        </Section>

        <Section title="4. Sign + verify">
          <button onClick={onSignAndVerify} disabled={!canSignAndVerify}>
            {debug.phase === "signing" && "Waiting on wallet signature…"}
            {debug.phase === "verify_loading" && "Verifying with BE…"}
            {debug.phase !== "signing" &&
              debug.phase !== "verify_loading" &&
              "Sign (personal_sign) + POST /verify"}
          </button>
          {nonce === null && (
            <p style={{ fontSize: 11.5, color: "var(--fg-dim)", margin: "6px 0 0" }}>
              Disabled until a nonce has been fetched.
            </p>
          )}
          <VerifyResultPanel debug={debug} />
        </Section>

        <PhaseLine phase={debug.phase} />
      </Disclosure>

      <Disclosure summary="Safe debug report">
        <div style={{ display: "flex", flexWrap: "wrap", gap: 8, margin: "12px 0 8px" }}>
          <Button size="sm" onClick={onCopySafeDebug}>
            Copy safe debug to clipboard
          </Button>
        </div>
        <p style={{ fontSize: 11.5, color: "var(--fg-dim)", margin: 0 }}>
          Copies <strong>only</strong> the public fields below — never the session token,
          signature, or HMAC secret.
        </p>
        {/* The routes this screen is bound to. They used to print as a
            "Bound to:" line directly under the page title — third in the
            reading order on the console's front door. They are operational
            evidence, not an operator-facing fact, so they moved here rather
            than being removed. */}
        {/* Paths only, no method prefix. The manual step-3 button's label is
            literally "POST /v2/auth/nonce", and `Login.test.tsx` matches it
            with a singular getByText — an element here whose exact text
            repeated it would throw in ten tests. */}
        <p style={{ fontSize: 11.5, color: "var(--fg-dim)", margin: "10px 0 0" }}>
          Bound to <code>/v2/auth/nonce</code> (canonical Gao SIWE) →{" "}
          <code>/v2/admin/owner/auth/verify</code> → <code>/v2/admin/bff/session</code>
        </p>
        <DebugTable debug={debug} />
      </Disclosure>

      <Disclosure summary="WalletConnect & build">
        <div style={{ display: "grid", gap: 12, marginTop: 12 }}>
          <WalletConnectStub />
          <div
            style={{
              display: "flex",
              flexWrap: "wrap",
              gap: 14,
              fontSize: 11,
              color: "var(--fg-dim)",
            }}
          >
            <span>
              build id: <code>{BUILD_ID}</code>
            </span>
            <span>
              commit: <code>{COMMIT_SHA}</code>
            </span>
            <span>
              built: <code>{BUILD_TIME}</code>
            </span>
          </div>
        </div>
      </Disclosure>
    </div>
  );
}

function PhaseLine({ phase }: { phase: Phase }) {
  return (
    <p style={{ marginTop: 8, fontSize: 12 }}>
      Phase: <code>{phase}</code>
    </p>
  );
}

function ChallengeResultPanel({ debug }: { debug: SafeDebug }) {
  const { challengeClicked, challengeInFlight, challengeOk, challengeError, challengeHttpStatus, challengeRequestIdPresent, beErrorCode, beErrorMessage } = debug;
  if (!challengeClicked) {
    return (
      <p style={{ fontSize: 11, color: "var(--fg-dim)", margin: "8px 0 0 0" }}>
        Click the button to request a challenge.
      </p>
    );
  }
  if (challengeInFlight) {
    return (
      <div
        style={{
          marginTop: 8,
          padding: 10,
          border: "1px solid var(--border)",
          borderRadius: 4,
          background: "var(--bg-elev)",
        }}
      >
        Loading challenge…
      </div>
    );
  }
  if (challengeOk === true) {
    return (
      <div
        role="status"
        style={{
          marginTop: 8,
          padding: 10,
          border: "1px solid var(--ok)",
          borderRadius: 4,
          background: "rgba(74,222,128,0.08)",
          color: "var(--ok)",
        }}
      >
        <strong>Challenge ready: requestId present.</strong> You can sign now.
      </div>
    );
  }
  // Failure path — always visible.
  return (
    <div
      role="alert"
      style={{
        marginTop: 8,
        padding: 10,
        border: "1px solid var(--danger)",
        borderRadius: 4,
        background: "rgba(255,100,100,0.08)",
        color: "var(--danger)",
      }}
    >
      <strong>Challenge FAILED.</strong>
      <div style={{ marginTop: 4, fontSize: 12 }}>
        HTTP status: <code>{challengeHttpStatus === null ? "—" : challengeHttpStatus}</code>{" "}
        · requestId present: <code>{challengeRequestIdPresent === null ? "—" : String(challengeRequestIdPresent)}</code>
      </div>
      {beErrorCode !== null && (
        <div style={{ marginTop: 4, fontSize: 12 }}>
          BE error code: <code>{beErrorCode}</code>
        </div>
      )}
      {beErrorMessage !== null && (
        <div style={{ marginTop: 4, fontSize: 12 }}>
          BE error message: <code>{beErrorMessage}</code>
        </div>
      )}
      {challengeError !== null && (
        <div style={{ marginTop: 4, fontSize: 12 }}>
          Detail: <code>{challengeError}</code>
        </div>
      )}
    </div>
  );
}

function VerifyResultPanel({ debug }: { debug: SafeDebug }) {
  const { verifyInFlight, verifyOk, verifyError, verifyHttpStatus, beErrorCode, beErrorMessage } = debug;
  if (verifyInFlight) {
    return (
      <div
        style={{
          marginTop: 8,
          padding: 10,
          border: "1px solid var(--border)",
          borderRadius: 4,
          background: "var(--bg-elev)",
        }}
      >
        Verifying…
      </div>
    );
  }
  if (verifyOk === true) {
    return (
      <div
        role="status"
        style={{
          marginTop: 8,
          padding: 10,
          border: "1px solid var(--ok)",
          borderRadius: 4,
          background: "rgba(74,222,128,0.08)",
          color: "var(--ok)",
        }}
      >
        <strong>Token minted.</strong> Probing /v2/admin/bff/session…
      </div>
    );
  }
  if (verifyOk === false) {
    return (
      <div
        role="alert"
        style={{
          marginTop: 8,
          padding: 10,
          border: "1px solid var(--danger)",
          borderRadius: 4,
          background: "rgba(255,100,100,0.08)",
          color: "var(--danger)",
        }}
      >
        <strong>Verify FAILED.</strong>
        <div style={{ marginTop: 4, fontSize: 12 }}>
          HTTP status: <code>{verifyHttpStatus === null ? "—" : verifyHttpStatus}</code>
        </div>
        {beErrorCode !== null && (
          <div style={{ marginTop: 4, fontSize: 12 }}>
            BE error code: <code>{beErrorCode}</code>
          </div>
        )}
        {beErrorMessage !== null && (
          <div style={{ marginTop: 4, fontSize: 12 }}>
            BE error message: <code>{beErrorMessage}</code>
          </div>
        )}
        {verifyError !== null && (
          <div style={{ marginTop: 4, fontSize: 12 }}>
            Detail: <code>{verifyError}</code>
          </div>
        )}
      </div>
    );
  }
  return null;
}

function NoWalletNotice({ detected }: { detected: boolean | null }) {
  if (detected !== false) return null;
  return (
    <div
      role="alert"
      style={{
        padding: 12,
        border: "1px solid var(--warn)",
        color: "var(--warn)",
        borderRadius: 4,
        background: "rgba(251,191,36,0.05)",
      }}
    >
      <strong>No EVM provider detected.</strong> Install MetaMask / Rabby /
      Frame and reload, or use the WalletConnect option below (if the
      operator has configured a project ID).
    </div>
  );
}

function InlineStatus({
  ok,
  loading,
  okMessage,
  idleMessage,
}: {
  ok: boolean;
  loading: boolean;
  okMessage: string;
  idleMessage: string;
}) {
  if (loading) {
    return (
      <p style={{ marginTop: 6, fontSize: 12 }}>Loading…</p>
    );
  }
  if (ok) {
    return (
      <p style={{ marginTop: 6, fontSize: 12, color: "var(--ok)" }}>
        ✓ {okMessage}
      </p>
    );
  }
  return (
    <p style={{ marginTop: 6, fontSize: 12, color: "var(--fg-dim)" }}>
      {idleMessage}
    </p>
  );
}

function DebugTable({ debug }: { debug: SafeDebug }) {
  const rows: Array<[string, string]> = [
    ["phase", debug.phase],
    [
      "provider detected",
      debug.walletProviderDetected === null
        ? "—"
        : debug.walletProviderDetected
          ? "true"
          : "false",
    ],
    ["wallet address", debug.walletAddress ?? "—"],
    ["wallet chainId", debug.walletChainId === null ? "—" : String(debug.walletChainId)],
    // The GaoSafe AUTHORITY chain, not a requirement on the wallet. Labelled
    // explicitly because a bare "expected chainId" in a pasted debug bundle is
    // exactly the confusion this screen just had to unlearn.
    ["authority chainId (server-side)", String(debug.expectedChainId)],
    ["provider error kind", debug.providerErrorKind ?? "—"],
    ["provider error code", debug.providerErrorCode === null ? "—" : String(debug.providerErrorCode)],
    ["provider error message", debug.providerErrorMessage ?? "—"],
    ["provider error data", debug.providerErrorData ?? "—"],
    ["ownerSubject", `"${debug.ownerSubject}" (${debug.ownerSubjectLength} chars)`],
    ["signer method", debug.signerMethod ?? "—"],
    ["challenge clicked", debug.challengeClicked ? "yes" : "no"],
    ["challenge in flight", debug.challengeInFlight ? "yes" : "no"],
    [
      "challenge HTTP status",
      debug.challengeHttpStatus === null ? "—" : String(debug.challengeHttpStatus),
    ],
    ["challenge ok", debug.challengeOk === null ? "—" : String(debug.challengeOk)],
    [
      "challenge requestId present",
      debug.challengeRequestIdPresent === null ? "—" : String(debug.challengeRequestIdPresent),
    ],
    ["challenge error", debug.challengeError ?? "—"],
    ["verify in flight", debug.verifyInFlight ? "yes" : "no"],
    [
      "verify HTTP status",
      debug.verifyHttpStatus === null ? "—" : String(debug.verifyHttpStatus),
    ],
    ["verify ok", debug.verifyOk === null ? "—" : String(debug.verifyOk)],
    ["verify error", debug.verifyError ?? "—"],
    [
      "session probe HTTP status",
      debug.sessionProbeHttpStatus === null ? "—" : String(debug.sessionProbeHttpStatus),
    ],
    [
      "session probe ok",
      debug.sessionProbeOk === null ? "—" : String(debug.sessionProbeOk),
    ],
    ["session probe error", debug.sessionProbeError ?? "—"],
    ["BE error code", debug.beErrorCode ?? "—"],
    ["BE error message", debug.beErrorMessage ?? "—"],
    ["last exception name", debug.lastExceptionName ?? "—"],
    ["last exception message", debug.lastExceptionMessage ?? "—"],
    ["sessionStorage row exists", debug.sessionStorageRowExists ? "yes" : "no"],
  ];
  return (
    <table style={{ width: "100%", borderCollapse: "collapse", marginTop: 8 }}>
      <tbody>
        {rows.map(([k, v]) => (
          <tr key={k} style={{ borderTop: "1px solid var(--border)" }}>
            <td style={{ padding: "4px 8px", color: "var(--fg-dim)", width: 220, fontSize: 11 }}>
              {k}
            </td>
            <td style={{ padding: "4px 8px", wordBreak: "break-all", fontSize: 11 }}>
              <code>{v}</code>
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function WalletConnectStub() {
  if (WALLETCONNECT_PROJECT_ID === null) {
    return (
      <div
        style={{
          padding: 10,
          border: "1px solid var(--warn)",
          color: "var(--warn)",
          borderRadius: 4,
          background: "rgba(251,191,36,0.05)",
        }}
      >
        <strong>WalletConnect option BLOCKED.</strong> Operator must set{" "}
        <code>VITE_WALLETCONNECT_PROJECT_ID</code> at build time. The SPA
        deliberately does NOT hardcode a fallback. Until set, regular browsers
        without an injected wallet cannot log in.
      </div>
    );
  }
  return (
    <div
      style={{
        padding: 10,
        border: "1px solid var(--warn)",
        color: "var(--warn)",
        borderRadius: 4,
        background: "rgba(251,191,36,0.05)",
      }}
    >
      <strong>WalletConnect project ID detected</strong>, but the provider
      library is not yet wired in this dev/test build. Operator follow-up: add{" "}
      <code>@walletconnect/ethereum-provider</code> + connector module in a
      separate PR. Login accepts any chain on the canonical SIWE allowlist.
    </div>
  );
}

function SessionProbePanel({ debug }: { debug: SafeDebug }) {
  if (debug.phase === "session_probing") {
    return (
      <div
        style={{
          marginTop: 8,
          padding: 10,
          border: "1px solid var(--border)",
          borderRadius: 4,
          background: "var(--bg-elev)",
        }}
      >
        Probing /v2/admin/bff/session with the freshly minted bearer…
      </div>
    );
  }
  if (debug.phase === "session_active") {
    return (
      <div
        role="status"
        style={{
          marginTop: 8,
          padding: 10,
          border: "1px solid var(--ok)",
          borderRadius: 4,
          background: "rgba(74,222,128,0.08)",
          color: "var(--ok)",
        }}
      >
        <strong>Session active.</strong> Navigating you back…
      </div>
    );
  }
  if (debug.phase === "session_probe_failed") {
    return (
      <div
        role="alert"
        style={{
          marginTop: 8,
          padding: 10,
          border: "1px solid var(--danger)",
          borderRadius: 4,
          background: "rgba(255,100,100,0.08)",
          color: "var(--danger)",
        }}
      >
        <strong>BE minted token but /v2/admin/bff/session rejected it.</strong>
        <div style={{ marginTop: 4, fontSize: 12 }}>
          HTTP status:{" "}
          <code>
            {debug.sessionProbeHttpStatus === null
              ? "—"
              : debug.sessionProbeHttpStatus}
          </code>
        </div>
        {debug.sessionProbeError !== null && (
          <div style={{ marginTop: 4, fontSize: 12 }}>
            Detail: <code>{debug.sessionProbeError}</code>
          </div>
        )}
      </div>
    );
  }
  return null;
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <fieldset
      style={{
        border: "1px solid var(--border)",
        borderRadius: 4,
        padding: 12,
      }}
    >
      <legend style={{ padding: "0 6px", color: "var(--fg-dim)" }}>{title}</legend>
      {children}
    </fieldset>
  );
}

const rowGap: React.CSSProperties = {
  display: "flex",
  flexWrap: "wrap",
  gap: 12,
  alignItems: "center",
};

function extractSessionToken(resp: VerifyResponseBody): string | null {
  if (typeof resp.sessionToken === "string" && resp.sessionToken !== "") {
    return resp.sessionToken;
  }
  if (typeof resp.token === "string" && resp.token !== "") {
    return resp.token;
  }
  const session = resp.session;
  if (
    session &&
    typeof session === "object" &&
    typeof session.token === "string" &&
    session.token !== ""
  ) {
    return session.token;
  }
  return null;
}

function extractExpiresAtMs(resp: VerifyResponseBody): number | null {
  const candidate = (resp as Record<string, unknown>).expiresAt;
  if (typeof candidate === "string" && candidate !== "") {
    const t = Date.parse(candidate);
    if (Number.isFinite(t)) return t;
  }
  if (typeof candidate === "number" && Number.isFinite(candidate)) {
    return candidate > 1e12 ? candidate : candidate * 1000;
  }
  return null;
}
